import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { getErrorNotificationMessage } from "../Helper/NotificationMessage";
import { payinReportClear, payinReportStart } from "../../store/slices/PaymentSlice";

const INITIAL_FILTERS = {
  from: "",
  to: "",
  status: "",
  date_filter_on: "",
};

const STATUS_OPTIONS = [
  { value: "INITIATED", label: "Initiated" },
  { value: "SUCCESS", label: "Success" },
  { value: "FAILED", label: "Failed" },
  { value: "CHARGEBACK", label: "Chargeback" },
  { value: "COMPLAINT", label: "Complaint" },
];

const DATE_FILTER_OPTIONS = [
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
];

const getISTDateString = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const dateParts = parts.reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

const getInclusiveDayDifference = (from, to) => {
  const fromDate = new Date(`${from}T00:00:00Z`);
  const toDate = new Date(`${to}T00:00:00Z`);
  return Math.floor((toDate - fromDate) / (24 * 60 * 60 * 1000)) + 1;
};

const formatNumber = (value, options = {}) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    ...options,
  }).format(Number(value || 0));

const PayinReportsIndex = () => {
  const dispatch = useDispatch();
  const payinReport = useSelector((state) => state.payment.payinReport);
  const maxDate = useMemo(() => getISTDateString(), []);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isReportLoading = hasSubmitted && payinReport.loading;

  const reportData = payinReport?.data || {};
  const reports = reportData?.reports || [];
  const totals = reports.reduce(
    (summary, report) => ({
      count: summary.count + Number(report.count || 0),
      total: summary.total + Number(report.total || 0),
      settlement: summary.settlement + Number(report.settlement || 0),
      commission: summary.commission + Number(report.commission || 0),
    }),
    { count: 0, total: 0, settlement: 0, commission: 0 }
  );

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const validateFilters = () => {
    if (!filters.from || !filters.to || !filters.status || !filters.date_filter_on) {
      getErrorNotificationMessage("All report filters are required.");
      return false;
    }

    if (filters.from > maxDate || filters.to > maxDate) {
      getErrorNotificationMessage("Future dates are not allowed.");
      return false;
    }

    if (filters.from > filters.to) {
      getErrorNotificationMessage("From date cannot be later than To date.");
      return false;
    }

    if (getInclusiveDayDifference(filters.from, filters.to) > 7) {
      getErrorNotificationMessage("Date range cannot be more than 7 days including From and To dates.");
      return false;
    }

    return true;
  };

  const fetchReports = (event) => {
    if (event) event.preventDefault();
    if (!validateFilters()) return;
    setHasSubmitted(true);
    dispatch(payinReportStart(filters));
  };

  useEffect(() => {
    dispatch(payinReportClear());
  }, [dispatch]);

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setHasSubmitted(false);
    dispatch(payinReportClear());
  };

  return (
    <div className="settlement-sec payin-report-sec">
      <div className="settlement-title-sec">
        <h3>PayIn Reports</h3>
      </div>

      <div className="payin-report-note">
        Choose From, To, Status, and Date Filter On to generate a PayIn report.
      </div>

      <Form className="payin-report-filter" onSubmit={fetchReports}>
        <Row className="g-3 align-items-end">
          <Col xl={2} lg={3} md={6}>
            <Form.Group>
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                name="from"
                value={filters.from}
                max={maxDate}
                onChange={updateFilter}
                required
              />
            </Form.Group>
          </Col>
          <Col xl={2} lg={3} md={6}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                name="to"
                value={filters.to}
                max={maxDate}
                onChange={updateFilter}
                required
              />
            </Form.Group>
          </Col>
          <Col xl={2} lg={3} md={6}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select name="status" value={filters.status} onChange={updateFilter} required>
                <option value="" disabled>
                  Select status
                </option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xl={2} lg={3} md={6}>
            <Form.Group>
              <Form.Label>Date Filter On</Form.Label>
              <Form.Select
                name="date_filter_on"
                value={filters.date_filter_on}
                onChange={updateFilter}
                required
              >
                <option value="" disabled>
                  Select date filter
                </option>
                {DATE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xl={2} lg={3} md={6}>
            <Button className="payin-report-submit" type="submit" disabled={isReportLoading}>
              {isReportLoading ? "Loading..." : "Generate"}
            </Button>
          </Col>
          <Col xl={2} lg={3} md={6}>
            <Button
              className="payin-report-clear"
              type="button"
              disabled={isReportLoading}
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Form>

      <div className="settlement-header-box payin-report-summary">
        <div className="settlement-header-card">
          <div></div>
          <div>
            <p>Total Count</p>
            <h3>{formatNumber(totals.count, { maximumFractionDigits: 0 })}</h3>
          </div>
        </div>
        <div className="settlement-header-card">
          <div></div>
          <div>
            <p>Total Amount</p>
            <h3>{formatNumber(totals.total)}</h3>
          </div>
        </div>
        <div className="settlement-header-card">
          <div></div>
          <div>
            <p>Settlement Amount</p>
            <h3>{formatNumber(totals.settlement)}</h3>
          </div>
        </div>
        <div className="settlement-header-card">
          <div></div>
          <div>
            <p>Commission Amount</p>
            <h3>{formatNumber(totals.commission)}</h3>
          </div>
        </div>
      </div>

      <div className="settlement-body-sec">
        <div className="settlement-table-sec table-responsive">
          <Table>
            <thead>
              <tr>
                <th>Date</th>
                <th className="text-end">Count</th>
                <th className="text-end">Total Amount</th>
                <th className="text-end">Settlement Amount</th>
                <th className="text-end">Commission Amount</th>
              </tr>
            </thead>
            <tbody>
              {isReportLoading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={5}>
                      <Skeleton height={24} />
                    </td>
                  </tr>
                ))
              ) : hasSubmitted && reports.length > 0 ? (
                reports.map((report) => (
                  <tr key={report.date}>
                    <td>{report.date}</td>
                    <td className="text-end">{formatNumber(report.count, { maximumFractionDigits: 0 })}</td>
                    <td className="text-end">{formatNumber(report.total)}</td>
                    <td className="text-end">{formatNumber(report.settlement)}</td>
                    <td className="text-end">{formatNumber(report.commission)}</td>
                  </tr>
                ))
              ) : !hasSubmitted ? (
                <tr>
                  <td colSpan={5} className="payin-report-empty">
                    Select filters and generate a report.
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={5}>
                    <p className="payin-report-empty">No records found.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default PayinReportsIndex;
